import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  Clock,
  AlertTriangle,
  ChevronRight,
  UserCheck,
  Bell,
  Flag,
} from "lucide-react";
import { waitingOnApi } from "@/lib/api";
import useRefreshOnFocus from "@/lib/useRefreshOnFocus";
import { initials, colorFor } from "@/lib/avatar";
import { useAuth } from "@/lib/auth";
import NudgeDialog from "@/components/launchpad/NudgeDialog";

// WaitingOnWidget — Feb 2026 (Iter 47).
//
// Inverse of MyPlateWidget. Shows tasks the current user CREATED but
// assigned to someone else that are still open. Every row has a Nudge
// button so producers can chase deliverables in one click without
// leaving the Launchpad.
//
// Backed by GET /api/waiting-on which filters plan_tasks where
// `created_by == me` AND `assigned_user_id != me` AND status != done.
// Legacy tasks (created before the created_by field shipped) won't
// appear because they have no delegator on record — this is
// intentional; the widget grows as new work is created.

const PHASE_PILL = {
  pre: "bg-teal-500/15 text-teal-300 border-teal-500/40",
  prod: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  post: "bg-violet-500/15 text-violet-300 border-violet-500/40",
};

function offsetLabel(t) {
  if (t.days_offset == null) return "no due";
  if (t.days_offset < 0) return `${Math.abs(t.days_offset)}d overdue`;
  if (t.days_offset === 0) return "due today";
  if (t.days_offset === 1) return "due tomorrow";
  return `due in ${t.days_offset}d`;
}

export default function WaitingOnWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgeTaskId, setNudgeTaskId] = useState(null);

  useRefreshOnFocus(() => {
    let cancelled = false;
    waitingOnApi
      .list()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <article
        data-testid="waiting-on-widget-loading"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5 flex items-center gap-2 text-zinc-500 text-sm"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading who&apos;s got what…
      </article>
    );
  }
  if (!data) return null;

  const tasks = (data.tasks || []).slice(0, 5);
  const totalOpen = (data.tasks || []).length;
  const lateCount = data.counts?.late || 0;

  // Nothing delegated: friendly note. We keep the widget rendered so
  // the Launchpad slot doesn't jump around between refreshes.
  if (totalOpen === 0) {
    return (
      <article
        data-testid="waiting-on-widget"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
        style={{ borderLeftColor: "#a78bfa", borderLeftWidth: 3 }}
      >
        <div className="text-overline text-violet-400/80 mb-1 flex items-center gap-1.5">
          <UserCheck className="w-3 h-3" /> Waiting On
        </div>
        <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
          No delegations pending.
        </h2>
        <p className="text-xs text-zinc-500 mt-1">
          Nothing you&apos;ve handed off is open right now. You&apos;re running a tight ship.
        </p>
      </article>
    );
  }

  const borderColor = lateCount > 0 ? "#fb7185" : "#a78bfa";

  return (
    <article
      data-testid="waiting-on-widget"
      className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
      style={{ borderLeftColor: borderColor, borderLeftWidth: 3 }}
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-overline text-violet-400/80 mb-1 flex items-center gap-1.5">
            <UserCheck className="w-3 h-3" /> Waiting On · {totalOpen} pending
          </div>
          <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
            {lateCount > 0 ? (
              <>{lateCount} late on your delegates</>
            ) : (
              <>What you&apos;ve handed off</>
            )}
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Open tasks you assigned to teammates. Nudge them without leaving this page.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/my-tasks")}
          data-testid="waiting-on-widget-open-all"
          className="text-[11px] font-mono uppercase tracking-wide text-zinc-400 hover:text-amber-500 transition-colors"
        >
          See all →
        </button>
      </div>

      <ul className="space-y-1.5" data-testid="waiting-on-widget-list">
        {tasks.map((t) => {
          const isLate = t.status === "late";
          const isMilestone = !!t.is_milestone;
          const phaseCls =
            PHASE_PILL[t.phase] || "bg-zinc-800 text-zinc-400 border-zinc-700";
          // Guardrail — the endpoint already filters out self-assigned
          // rows, but if the shape ever changes we don't want to expose
          // a Nudge button that would send a chase email to yourself.
          const canNudge = !!t.assigned_user_id && t.assigned_user_id !== user?.id;
          return (
            <li
              key={t.id}
              data-is-milestone={isMilestone ? "true" : undefined}
              className={`group flex items-center rounded-sm transition-colors ${
                isMilestone
                  ? "hover:bg-violet-950/40 bg-violet-950/15 border border-violet-500/25"
                  : "hover:bg-zinc-900/60"
              }`}
            >
              <button
                type="button"
                data-testid={`waiting-on-row-${t.id}`}
                onClick={() =>
                  navigate(`/projects/${t.project_id}/plan?task=${t.id}`)
                }
                className="flex-1 min-w-0 text-left flex items-center gap-3 py-2 pl-2 pr-1"
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    isLate
                      ? "bg-rose-400"
                      : t.status === "due_soon"
                      ? "bg-amber-400"
                      : "bg-violet-400"
                  }`}
                />
                {isMilestone ? (
                  <span
                    data-testid={`waiting-on-milestone-badge-${t.id}`}
                    className="px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border shrink-0 bg-violet-500/25 text-violet-100 border-violet-500/60 inline-flex items-center gap-1 font-bold"
                    title="Milestone"
                  >
                    <Flag className="w-2.5 h-2.5" />
                    Milestone
                  </span>
                ) : (
                  <span
                    className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border shrink-0 ${phaseCls}`}
                  >
                    {t.phase || "—"}
                  </span>
                )}
                <span
                  className={`flex-1 text-sm truncate min-w-0 ${
                    isMilestone ? "text-violet-100 font-medium" : "text-zinc-200"
                  }`}
                >
                  {t.name}
                </span>
                {t.assigned_user_id && (
                  <span
                    data-testid={`waiting-on-assignee-${t.id}`}
                    className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-zinc-300 max-w-[160px] shrink-0"
                    title={`Assigned to ${t.assigned_user_name || "—"}`}
                  >
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-mono font-bold text-zinc-950 shrink-0"
                      style={{ backgroundColor: colorFor(t.assigned_user_id) }}
                    >
                      {initials(t.assigned_user_name || "?")}
                    </span>
                    <span className="truncate">
                      {(t.assigned_user_name || "").split(" ")[0] || "—"}
                    </span>
                  </span>
                )}
                <span className="text-[10px] text-zinc-500 font-mono truncate hidden sm:inline max-w-[160px]">
                  {t.project_name}
                </span>
                <span
                  className={`text-[10px] font-mono uppercase tracking-wide flex items-center gap-1 shrink-0 ${
                    isLate
                      ? "text-rose-300"
                      : t.status === "due_soon"
                      ? "text-amber-300"
                      : "text-zinc-500"
                  }`}
                >
                  {isLate ? (
                    <AlertTriangle className="w-3 h-3" />
                  ) : (
                    <Clock className="w-3 h-3" />
                  )}
                  {offsetLabel(t)}
                </span>
              </button>
              <button
                type="button"
                data-testid={`waiting-on-nudge-${t.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canNudge) return;
                  setNudgeTaskId(t.id);
                }}
                disabled={!canNudge}
                title={
                  !canNudge
                    ? "Nothing to nudge"
                    : `Nudge ${t.assigned_user_name || "the assignee"}`
                }
                className={`mx-1 inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wide border transition-colors shrink-0 ${
                  canNudge
                    ? "border-zinc-700 text-zinc-300 hover:border-amber-500/60 hover:text-amber-400 hover:bg-amber-500/10"
                    : "border-zinc-800 text-zinc-600 cursor-not-allowed"
                }`}
              >
                <Bell className="w-3 h-3" />
                Nudge
              </button>
              <ChevronRight
                className="w-3.5 h-3.5 text-zinc-700 group-hover:text-amber-400 transition-colors shrink-0 mr-2 pointer-events-none"
              />
            </li>
          );
        })}
      </ul>
      <NudgeDialog
        taskId={nudgeTaskId}
        open={!!nudgeTaskId}
        onOpenChange={(o) => !o && setNudgeTaskId(null)}
      />
    </article>
  );
}
